import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, computed, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GalleryDto, GalleryTheme, Section, TenantDto } from '@nusign/picflow-backend-types';
import { fontSizeMap } from '../../../consts';
import { debounce } from '../../../lib/debounce';
import { fixContentEditableStyles } from '../../../lib/fix-contenteditable-styles';
import { getHtmlText } from '../../../lib/get-html-text';
import { loadFont } from '../../../lib/load-font';
import { CleanUserGeneratedHtmlService } from '../../../services/clean-user-generated-html.service';
import { DialogService } from '../../../services/dialog.service';
import { SectionService } from '../../../services/section.service';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';
import { ApplyTypographyStylesDirective } from '../../directives/apply-typography-styles.directive';
import { GalleryThemeMenuComponent } from '../../menus/gallery-theme-menu/gallery-theme-menu.component';

@Component({
  selector: 'app-edit-section-text',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [WysiwygDirective, FormsModule, ApplyTypographyStylesDirective],
  templateUrl: './edit-section-text.component.html',
  styleUrl: './edit-section-text.component.scss',
})
export class EditSectionTextComponent implements OnInit {
  private dialog = inject(DialogService);
  private sectionService = inject(SectionService);
  private cleanUserGeneratedHtmlService = inject(CleanUserGeneratedHtmlService);
  private check = inject(ChangeDetectorRef);
  readonly section = input<Section>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly theme = input.required<GalleryTheme>();
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  readonly darkBackground = input<boolean>(undefined);

  tenant = computed(() => {
    return this.gallery()?.tenant as TenantDto;
  });

  textTitle: string;
  textParagraph: string;

  titleFontSize = computed(() => {
    const section = this.section();
    const theme = this.theme();
    if (!section || !theme) return;
    const paragraphSize = section.text_title_typography.font_size || theme.section.title_typography.font_size;

    return <'small' | 'medium' | 'large'>(
      (Object.keys(fontSizeMap).find(key => fontSizeMap[key] === paragraphSize) || 'small')
    );
  });

  paragraphFontSize = computed(() => {
    const section = this.section();
    const theme = this.theme();
    if (!section || !theme) return;
    const paragraphSize = section.text_paragraph_typography.font_size || theme.section.paragraph_typography.font_size;

    return <'small' | 'medium' | 'large'>(
      (Object.keys(fontSizeMap).find(key => fontSizeMap[key] === paragraphSize) || 'small')
    );
  });

  ngOnInit() {
    const section = this.section();
    this.textTitle = this.cleanUserGeneratedHtmlService.clean(section.text_title);
    if (this.textTitle === '__EMPTY_TITLE__') this.textTitle = '';
    section.text_paragraph = this.cleanUserGeneratedHtmlService.clean(section.text_paragraph);
    this.textParagraph = section.text_paragraph || '';
    this.check.detectChanges();

    loadFont(section.text_title_typography.font_family);
    loadFont(section.text_paragraph_typography.font_family);
  }

  openThemeMenu(typographyMenu: string) {
    this.dialog.open(GalleryThemeMenuComponent, {
      gallery: this.gallery(),
      subPage: 'typography',
      openTypographyMenu: typographyMenu,
    });
  }

  onParagraphFontChange(font: string) {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_paragraph_typography: {
          ...section.text_paragraph_typography,
          font_family: font,
        },
      })
      .subscribe();
  }

  onParagraphFontSizeChange(size: 'small' | 'medium' | 'large') {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_paragraph_typography: {
          ...section.text_paragraph_typography,
          font_size: fontSizeMap[size],
        },
      })
      .subscribe();
  }
  onParagraphFontWeightChange(weight: string) {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_paragraph_typography: { ...section.text_paragraph_typography, font_weight: weight },
      })
      .subscribe();
  }

  onTitleFontChange(font: string) {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_title_typography: {
          ...section.text_title_typography,
          font_family: font,
        },
      })
      .subscribe();
  }

  onTitleFontSizeChange(size: 'small' | 'medium' | 'large') {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_title_typography: { ...section.text_title_typography, font_size: fontSizeMap[size] },
      })
      .subscribe();
  }

  onTitleFontWeightChange(weight: string) {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_title_typography: { ...section.text_title_typography, font_weight: weight },
      })
      .subscribe();
  }

  onAlignmentChange(alignment: 'left' | 'center' | 'right') {
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_title_typography: {
          ...section.text_title_typography,
          text_alignment: alignment,
        },
        text_paragraph_typography: {
          ...section.text_paragraph_typography,
          text_alignment: alignment,
        },
      })
      .subscribe();
  }

  onTitleTextColorChange(color: string) {
    const section = this.section();
    this.sectionService.optimisticUpdateSection(this.section(), {
      text_title_typography: {
        ...section.text_title_typography,
        font_color: color,
      },
    });
    this.saveTitleTextColorDebounced();
  }

  onParagraphTextColorChange(color: string) {
    const section = this.section();
    this.sectionService.optimisticUpdateSection(section, {
      text_paragraph_typography: {
        ...section.text_paragraph_typography,
        font_color: color,
      },
    });
    this.saveParagraphTextColorDebounced();
  }

  private saveTitleTextColorDebounced = debounce(() => {
    const section = this.section();
    this.sectionService
      .updateSection(this.section(), {
        text_title_typography: {
          ...section.text_title_typography,
        },
      })
      .subscribe();
  }, 500);

  private saveParagraphTextColorDebounced = debounce(() => {
    const section = this.section();
    this.sectionService
      .updateSection(this.section(), {
        text_paragraph_typography: {
          ...section.text_paragraph_typography,
        },
      })
      .subscribe();
  }, 500);

  private compare(a: string, b: string) {
    return a?.replace('__EMPTY_TITLE__', '') === b?.replace('__EMPTY_TITLE__', '');
  }

  saveText() {
    const textTitle = getHtmlText(this.textTitle);
    const textParagraph = fixContentEditableStyles(this.textParagraph);
    const section = this.section();
    if (this.compare(textTitle, section.text_title) && this.compare(textParagraph, section.text_paragraph)) return;

    this.sectionService.optimisticUpdateSection(section, {
      text_title: textTitle || '__EMPTY_TITLE__',
      text_paragraph: textParagraph,
    });
    this.saveTextDebounced();
  }

  private saveTextDebounced = debounce(() => {
    const textTitle = getHtmlText(this.textTitle);
    const textParagraph = fixContentEditableStyles(this.textParagraph);
    const section = this.section();
    this.sectionService
      .updateSection(section, {
        text_title: textTitle || '__EMPTY_TITLE__',
        text_paragraph: textParagraph,
      })
      .subscribe();
  }, 500);
}
